"use client";

import React, { useEffect, useState, useContext } from "react";
import {
	Box,
	Button,
	TextField,
	Typography,
	Autocomplete,
	Switch,
	FormControlLabel,
	IconButton,
	Paper,
	CircularProgress,
	Alert,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import { AuthContext } from "context";
import apiService from "../../services/api-service";
import toast from "react-hot-toast";

const PianoControlliAdd = () => {
	const { id } = useParams();
	const isEdit = Boolean(id);
	const navigate = useNavigate();
	const authContext = useContext(AuthContext);

	const [loading, setLoading] = useState(false);
	const [wbsData, setWbsData] = useState([]);
	const [areaData, setAreaData] = useState([]);
	const [formData, setFormData] = useState({
		wbs_id: null,
		controlli: [""],
		riferimenti: "",
		registrazione: "",
		applicabile: true,
	});
	const [errors, setErrors] = useState({});

	const handleSetNotification = (message, type) => {
		if (type === "success") toast.success(message);
		else if (type === "error") toast.error(message);
		else if (type === "warning") toast.warning(message);
	};

	const fetchWbsData = async () => {
		try {
			const response = await apiService.get(
				"/jobs-management/",
				{
					options: true,
					area_id: localStorage.getItem("areaSelected"),
				},
				{}
			);
			setWbsData(response || []);
		} catch (error) {
			console.error("Error fetching WBS data:", error);
			setWbsData([]);
		}
	};

	const fetchControlloData = async () => {
		if (!isEdit) return;

		setLoading(true);
		try {
			const response = await apiService.get(
				"/piano-controlli/",
				{
					id: id,
				},
				{}
			);
			console.log("Edit data response:", response);
			setFormData(response);
		} catch (error) {
			handleSetNotification("Errore nel caricamento dei dati", "error");
			navigate("/piano-controlli");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchWbsData();
		fetchControlloData();
	}, [id]);

	const validateForm = () => {
		const newErrors = {};

		if (!formData.wbs_id) {
			newErrors.wbs_id = "WBS è obbligatorio";
		}

		if (!formData.controlli.some((controllo) => controllo.trim())) {
			newErrors.controlli = "Almeno un controllo è obbligatorio";
		}

		if (!formData.riferimenti.trim()) {
			newErrors.riferimenti = "Riferimenti è obbligatorio";
		}

		if (!formData.registrazione.trim()) {
			newErrors.registrazione = "Registrazione è obbligatorio";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validateForm()) {
			handleSetNotification("Compila tutti i campi obbligatori", "error");
			return;
		}

		setLoading(true);
		try {
			const dataToSend = {
				...formData,
				controlli: formData.controlli.filter((controllo) =>
					controllo.trim()
				),
				area_id: localStorage.getItem("areaSelected"),
			};

			if (isEdit) {
				await apiService.put(
					"/piano-controlli/",
					{},
					{ ...dataToSend, id: id }
				);
				handleSetNotification(
					"Controllo aggiornato con successo",
					"success"
				);
			} else {
				await apiService.post("/piano-controlli/", {}, dataToSend);
				handleSetNotification(
					"Controllo creato con successo",
					"success"
				);
			}

			navigate("/piano-controlli");
		} catch (error) {
			console.log("error", error);
			handleSetNotification(
				isEdit
					? "Errore nell'aggiornamento del controllo"
					: "Errore nella creazione del controllo",
				"error"
			);
		} finally {
			setLoading(false);
		}
	};

	const handleControlloChange = (index, value) => {
		const newControlli = [...formData.controlli];
		newControlli[index] = value;
		setFormData({ ...formData, controlli: newControlli });

		if (errors.controlli) {
			setErrors({ ...errors, controlli: null });
		}
	};

	const addControllo = () => {
		setFormData({
			...formData,
			controlli: [...formData.controlli, ""],
		});
	};

	const removeControllo = (index) => {
		if (formData.controlli.length > 1) {
			const newControlli = formData.controlli.filter(
				(_, i) => i !== index
			);
			setFormData({ ...formData, controlli: newControlli });
		}
	};

	const getAreaOptions = () => {
		return areaData.map((area) => ({
			label: area.name,
			value: area.id,
			...area,
		}));
	};

	const getWbsOptions = () => {
		return wbsData
			.filter((item) => item.id.includes("-job")) // Only include jobs, not motivations
			.map((item) => {
				// Extract the actual job ID (remove "-job" suffix)
				const actualId = item.id.replace("-job", "");
				return {
					label: item.name,
					value: parseInt(actualId),
					id: parseInt(actualId),
					name: item.name,
				};
			});
	};

	if (loading && isEdit) {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "400px",
					}}
				>
					<CircularProgress size={120} sx={{ color: "#2b84ea" }} />
				</Box>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box sx={{ padding: "20px" }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						marginBottom: "24px",
						gap: "16px",
					}}
				>
					<IconButton
						onClick={() => navigate("/piano-controlli")}
						sx={{
							backgroundColor: "#f5f5f5",
							"&:hover": { backgroundColor: "#e0e0e0" },
						}}
					>
						<ArrowBackIcon />
					</IconButton>
					<Typography
						variant="h4"
						sx={{
							fontWeight: 600,
							fontSize: "1.75rem",
						}}
					>
						{isEdit ? "Modifica Controllo" : "Aggiungi Controllo"}
					</Typography>
				</Box>

				<Paper sx={{ padding: "24px", borderRadius: "12px" }}>
					<form onSubmit={handleSubmit}>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: "24px",
							}}
						>
							{/* WBS Selection */}
							<Autocomplete
								options={getWbsOptions()}
								value={
									getWbsOptions().find(
										(option) =>
											option.value === formData.wbs_id
									) || null
								}
								onChange={(event, newValue) => {
									setFormData({
										...formData,
										wbs_id: newValue?.value || null,
									});
									if (errors.wbs_id) {
										setErrors({ ...errors, wbs_id: null });
									}
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="WBS *"
										error={!!errors.wbs_id}
										helperText={errors.wbs_id}
										fullWidth
									/>
								)}
								isOptionEqualToValue={(option, value) =>
									option.value === value.value
								}
							/>

							{/* Controlli List */}
							<Box>
								<Typography
									variant="h6"
									sx={{ marginBottom: "12px" }}
								>
									Controlli *
								</Typography>
								{errors.controlli && (
									<Alert
										severity="error"
										sx={{ marginBottom: "12px" }}
									>
										{errors.controlli}
									</Alert>
								)}
								{formData.controlli.map((controllo, index) => (
									<Box
										key={index}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: "12px",
											marginBottom: "12px",
										}}
									>
										<TextField
											label={`Controllo ${index + 1}`}
											value={controllo}
											onChange={(e) =>
												handleControlloChange(
													index,
													e.target.value
												)
											}
											fullWidth
											variant="outlined"
										/>
										{formData.controlli.length > 1 && (
											<IconButton
												onClick={() =>
													removeControllo(index)
												}
												color="error"
												sx={{ minWidth: "40px" }}
											>
												<DeleteIcon />
											</IconButton>
										)}
									</Box>
								))}
								<Button
									startIcon={<AddIcon />}
									onClick={addControllo}
									variant="outlined"
									color="secondary"
									sx={{
										textTransform: "none",
										color: "#424242",
									}}
								>
									Aggiungi Controllo
								</Button>
							</Box>

							{/* Riferimenti */}
							<TextField
								label="Riferimenti *"
								value={formData.riferimenti}
								onChange={(e) => {
									setFormData({
										...formData,
										riferimenti: e.target.value,
									});
									if (errors.riferimenti) {
										setErrors({
											...errors,
											riferimenti: null,
										});
									}
								}}
								error={!!errors.riferimenti}
								helperText={errors.riferimenti}
								fullWidth
								multiline
								rows={3}
							/>

							{/* Registrazione */}
							<TextField
								label="Registrazione *"
								value={formData.registrazione}
								onChange={(e) => {
									setFormData({
										...formData,
										registrazione: e.target.value,
									});
									if (errors.registrazione) {
										setErrors({
											...errors,
											registrazione: null,
										});
									}
								}}
								error={!!errors.registrazione}
								helperText={errors.registrazione}
								fullWidth
								multiline
								rows={2}
							/>

							{/* Applicabile Switch */}
							<FormControlLabel
								control={
									<Switch
										checked={formData.applicabile}
										onChange={(e) =>
											setFormData({
												...formData,
												applicabile: e.target.checked,
											})
										}
										color="primary"
									/>
								}
								label="Applicabile"
								sx={{ alignSelf: "flex-start" }}
							/>

							{/* Submit Button */}
							<Box
								sx={{
									display: "flex",
									gap: "12px",
									justifyContent: "flex-end",
								}}
							>
								<Button
									variant="outlined"
									onClick={() => navigate("/piano-controlli")}
									sx={{
										textTransform: "none",
										color: "#424242",
									}}
								>
									Annulla
								</Button>
								<Button
									type="submit"
									variant="contained"
									startIcon={
										loading ? (
											<CircularProgress
												size={20}
												color="inherit"
											/>
										) : (
											<SaveIcon />
										)
									}
									disabled={loading}
									sx={{
										textTransform: "none",
										color: "white!important",
									}}
								>
									{loading
										? "Salvando..."
										: isEdit
											? "Aggiorna"
											: "Salva"}
								</Button>
							</Box>
						</Box>
					</form>
				</Paper>
			</Box>
		</DashboardLayout>
	);
};

export default PianoControlliAdd;
